import React from "react";
import { withRouter } from "react-router-dom";
import { withRefluxStore } from "../../../../Stores";
import ProfilesStore from "../../Profile.store";
import {
  ArrowContainer,
  Card,
  CardText,
  CategoryContainer,
  customStyles,
  Header,
  phoneNumberTestModal,
  SearchBox,
  FloatingBtn,
  formButtonStyles,
  phoneIconStyles
} from "./ProfileCategories.styled";
import CreatableSelect from "react-select/creatable";
import { useTranslation } from "react-i18next";
import { Group } from "../../../../styles/layout";
import { HelpCircle } from "@untitledui/icons/HelpCircle";
import { ArrowLeft } from "@untitledui/icons/ArrowLeft";
import { LinkExternal01 } from "@untitledui/icons/LinkExternal01";
import { Phone01 } from "@untitledui/icons/Phone01";
import { AnimatePresence } from "framer-motion";
import { FadeInAnimation_Opacity_FromRight } from "../../../../animations/animations";
import { useState } from "react";
import { ActionButton, Floating, TestPhoneModal, Modal } from "@talentbait/shared-components";

import Actions from "../../../../Actions";
import { useCallback } from "react";
import { EmptyStateText, iconTextButtonStyles, Skeleton, SkeletonSection } from "../Profile.styled";
import logger from "../../../../utils/LoggerFrontend.js";
import { FloatingContainer } from "../profile-summary/ProfileSummary.styled.js";
import { PlusCircle } from "@untitledui/icons/PlusCircle";
import { SearchMd } from "@untitledui/icons/SearchMd";
import { DELIVERY_METHOD } from "../../Profile.constants.js";

const createOption = (label) => ({
  label,
  value: label
});

const ProfileCategories = ({
  // store
  userProfile,
  isLoadingCategories,
  categories,
  profile,
  profileMeta,
  history
}) => {
  const { t } = useTranslation();

  const [isTestPhoneModalOpen, setIsTestPhoneModalOpen] = useState(false);
  const [isGeneratingForm, setIsGeneratingForm] = useState(false);

  const [inputValue, setInputValue] = useState("");
  const [searchTags, setSearchTags] = useState([]);

  const searchInput = useCallback(() => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;

    const exists = searchTags.some(({ value }) => value.toLowerCase() === trimmed.toLowerCase());
    if (!exists) {
      setSearchTags((prev) => [...prev, createOption(trimmed)]);
    }

    setInputValue("");
  }, [inputValue, searchTags]);

  const handleKeyDown = useCallback(
    (event) => {
      if (!inputValue) return;
      if (event.key === "Enter" || event.key === "Tab") {
        event.preventDefault();

        searchInput();
      }
    },
    [inputValue, searchInput]
  );

  const openIntercom = () => {
    if (window.Intercom) {
      window.Intercom("show");
    }
  };

  const createForm = () => {
    const failRef = Actions.generateWhatsappForm.failed.listen(() => {
      setIsGeneratingForm(false);
    });

    logger.log("ProfileCategories:createForm", `generate whatsapp form`, profile.id);
    Actions.generateWhatsappForm(profile.id);
    setIsGeneratingForm(true);

    return () => {
      failRef();
    };
  };

  const openScreeningQuestions = useCallback(() => {
    history.push("/configuration/applicant");
  }, [history]);

  const addCategoryToRequirements = (selectedCategory) => {
    const categoryExists = profile.requirements?.some(
      ({ category }) => category === selectedCategory.name
    );

    if (categoryExists) {
      Actions.addNotification(t("A requirement with this category already exists"), "error");
      return;
    }

    const newRequirement = {
      categoryId: selectedCategory.categoryId,
      category: selectedCategory.name,
      importance: null,
      isAutomated: false,
      deliveryMethod: DELIVERY_METHOD.WHATSAPP_FORM,
      type: selectedCategory.type,
      options: []
    };

    logger.log("ProfileCategories:addCategoryToRequirements", `adding new requirement`, {
      selectedCategory,
      newRequirement
    });

    const update = {
      requirements: [...(profile.requirements || []), newRequirement]
    };

    Actions.patchProfile(profile.id, update);
  };

  if (profileMeta === "initial" || isLoadingCategories) {
    return (
      <SkeletonSection>
        <Skeleton height="17px" margin="0 0 8px" />
        {Array.from({ length: 7 }).map((_, idx) => (
          <Skeleton key={idx} height="40px" />
        ))}
      </SkeletonSection>
    );
  }

  const filteredCategories = categories
    .filter((category) => !profile?.requirements?.some((req) => req.category === category.name))
    .filter((category) => {
      const allFilters = [...searchTags];
      if (inputValue.trim()) {
        allFilters.push(createOption(inputValue.trim()));
      }

      if (allFilters.length === 0) return true;

      return allFilters.some((filter) =>
        category.name.toLowerCase().includes(filter.value.toLowerCase())
      );
    })
    // The .reduce ensures that only the first occurrence of each unique category.name is included in filteredCategories.
    .reduce((acc, curr) => {
      if (!acc.some((cat) => cat.name === curr.name)) {
        acc.push(curr);
      }
      return acc;
    }, []);

  return (
    <Group>
      <CategoryContainer>
        <Header>{t("Add information")}</Header>
        <SearchBox>
          <CreatableSelect
            id="category-search-input"
            components={{ DropdownIndicator: null }}
            inputValue={inputValue}
            value={searchTags}
            onChange={(newValue) => setSearchTags(newValue)}
            onInputChange={(newValue) => setInputValue(newValue)}
            onKeyDown={handleKeyDown}
            placeholder={t("Type something to filter...")}
            menuIsOpen={false}
            styles={customStyles}
            isClearable
            isMulti
          />
          <SearchMd id="search-md-icon" onClick={searchInput} />
        </SearchBox>
        {filteredCategories.length === 0 && searchTags.length === 0 ? (
          <EmptyStateText>{t("No Categories found.")}</EmptyStateText>
        ) : (
          <Group gap="8px" width="100%">
            <AnimatePresence initial={false}>
              {filteredCategories.map((category) => (
                <Card
                  key={category.name}
                  {...FadeInAnimation_Opacity_FromRight}
                  onClick={() => addCategoryToRequirements(category)}
                >
                  <Group direction="row" align="center">
                    <ArrowContainer>
                      <ArrowLeft color="var(--primary-blue)" />
                    </ArrowContainer>
                    <CardText>{category.name}</CardText>
                  </Group>
                  <HelpCircle id="help-circle-icon" color="var(--primary-blue-300)" size={16} />
                </Card>
              ))}
            </AnimatePresence>
          </Group>
        )}
        <Group direction="row" justify="center" width="100%" gap="15px" margin="30px 0 0">
          <ActionButton
            icon={<LinkExternal01 size="20px" />}
            variant="link"
            text={t("Browse all")}
            onClick={openScreeningQuestions}
            styles={iconTextButtonStyles}
          />

          <ActionButton
            icon={<PlusCircle size="20px" />}
            variant="link"
            text={t("Add new")}
            onClick={openIntercom}
            styles={iconTextButtonStyles}
          />
        </Group>
      </CategoryContainer>

      <Group direction="row" width="100%" gap="15px" align="center">
        <Floating
          enabled={isGeneratingForm}
          trigger={
            <FloatingBtn>
              <ActionButton
                variant="secondary"
                onClick={createForm}
                text={t("Generate Whatsapp form")}
                disabled={isGeneratingForm}
                styles={formButtonStyles}
              />
            </FloatingBtn>
          }
          content={
            <FloatingContainer width="200px">
              {t(
                "Testing with your phone is currently disabled while the form is reviewed by WhatsApp. Check back in 5 minutes."
              )}
            </FloatingContainer>
          }
        />

        <ActionButton
          icon={<Phone01 size="20px" />}
          variant="secondary"
          text={t("Test Questions")}
          onClick={() => setIsTestPhoneModalOpen(true)}
          disabled={isGeneratingForm}
          styles={phoneIconStyles}
        />
      </Group>

      <Modal
        isOpen={isTestPhoneModalOpen}
        setIsOpen={setIsTestPhoneModalOpen}
        styles={phoneNumberTestModal}
      >
        <TestPhoneModal
          orgaId={userProfile.organizationId}
          profileId={profile.id}
          isChatbotEnabled={true}
          setIsTestPhoneModalOpen={setIsTestPhoneModalOpen}
        />
      </Modal>
    </Group>
  );
};

export default withRouter(
  withRefluxStore(
    ProfileCategories,
    [ProfilesStore],
    ["userProfile", "categories", "profile", "isLoadingCategories", "profileMeta"]
  )
);
